import type {Appareil, CertificatRamonage, ICompany, ICustomer, IPDFStyle} from "@mytour/shared";
import {getAppareilTypeLabel, getCompanyDisplayName, getLabel} from "@mytour/shared";
import {Document, Image, Link, Page, StyleSheet, Text, View} from "@react-pdf/renderer";
import dayjs from "dayjs";
import {PDFSquareLogo} from "../common/PDFSquareLogo";
import {
  RamonagePhotos,
  assertRamonagePhotosRendered,
  validateRamonagePhotos,
} from "./RamonagePhotos";

type Props = {
  data: CertificatRamonage;
  company: ICompany;
  customer: ICustomer;
  pdfStyle: IPDFStyle;
  intervenantName?: string;
};

export function RamonagePdfReport({data, company, customer, pdfStyle, intervenantName}: Props) {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: "white",
      padding: "8mm 10mm",
      paddingBottom: "22mm",
      fontSize: 8,
      fontFamily: "Roboto",
      color: "#333",
    },
    sectionTitle: {
      fontSize: 9,
      fontWeight: "bold",
      backgroundColor: pdfStyle.color2 ?? "#e0e0e0",
      color: pdfStyle.color ?? "#333",
      padding: "3 6",
      marginBottom: 4,
      marginTop: 12,
    },
    cellLabel: {
      fontSize: 7,
      color: "#666",
      marginBottom: 2,
    },
    cellValue: {
      fontSize: 8,
      fontWeight: "bold",
      marginLeft: 4,
    },
    gridRow: {
      flexDirection: "row",
      borderBottom: "0.5pt solid #ccc",
      paddingVertical: 2,
    },
    gridCell: {
      flex: 1,
      padding: "2 4",
    },
    text: {
      fontSize: 10,
      marginBottom: 1,
    },
    textSmall: {
      fontSize: 9,
      marginBottom: 1,
    },
    signatureSection: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: 8,
    },
    signatureBlock: {
      width: "45%",
      alignItems: "center",
    },
    signatureImage: {
      width: 120,
      height: 60,
      objectFit: "contain",
    },
    footer: {
      marginTop: "auto",
      borderTop: "1pt solid #ccc",
      paddingTop: 4,
      fontSize: 8,
      color: "#333",
      textAlign: "center",
    },
    bandeau: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginTop: 12,
    },
    bandeauLabel: {
      flexShrink: 0,
      fontSize: 9.5,
      fontWeight: "bold",
      backgroundColor: "#111",
      color: "#fff",
      padding: "3 8",
    },
    bandeauReminder: {
      flex: 1,
      minWidth: 0,
      fontSize: 8,
      color: "#666",
    },
    continuation: {
      marginTop: "auto",
      textAlign: "center",
      color: "#888",
      fontSize: 7.6,
      fontStyle: "italic",
      paddingVertical: 8,
    },
    pageNumber: {
      position: "absolute",
      right: "10mm",
      bottom: "4mm",
      fontSize: 7,
      color: "#888",
    },
  });

  const dateParsed = dayjs(data.date).format("DD/MM/YYYY");
  const {legal} = company;
  const companyAddress = `${legal.administrativeAddress.streetAddress}, ${legal.administrativeAddress.zipCode} ${legal.administrativeAddress.city}`;
  const appareilCount = data.appareils.length;
  const photoCount = data.appareils.reduce(
    (count, appareil) => count + (appareil.photos?.length ?? 0),
    0
  );
  const hasPhotos = photoCount > 0;
  const repeatedLayout = appareilCount > 1 || hasPhotos;
  validateRamonagePhotos(data.appareils);
  const photoRenderProps = hasPhotos
    ? {onRender: (result: unknown) => assertRamonagePhotosRendered(result, photoCount)}
    : {};

  return (
    <Document {...photoRenderProps}>
      {data.appareils.map((appareil, index) => {
        const isLastAppareil = index === appareilCount - 1;
        const isSecondToLastAppareil = index === appareilCount - 2;

        // react-pdf teste la PRÉSENCE de la clé `fixed` dans les props (pas sa valeur) pour
        // décider si un nœud franchit un saut de page — `fixed={undefined}` la laisserait
        // présente et changerait le calcul de pagination même à 1 appareil. Le spread
        // conditionnel omet la clé elle-même, seule façon de garder le rendu identique.
        const fixedProps = repeatedLayout ? {fixed: true as const} : {};
        const appareilBanner = (
          <View style={styles.bandeau}>
            <Text style={styles.bandeauLabel}>
              APPAREIL {index + 1} SUR {appareilCount}
            </Text>
            <Text style={styles.bandeauReminder}>
              {getAppareilTypeLabel(appareil)} · {appareil.appareilEmplacement}
            </Text>
          </View>
        );

        return (
          <Page key={index} size="A4" style={styles.page}>
            <View {...fixedProps}>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <PDFSquareLogo pdfStyle={pdfStyle} style={{width: "20mm", marginRight: "3mm"}} />
                <View style={{flex: 1}}>
                  <Text style={{fontSize: 14, fontWeight: "bold", marginBottom: 2}}>
                    {getCompanyDisplayName(company.commercialName, legal.legalName)}
                  </Text>
                  <Text style={{fontSize: 11, color: "#444"}}>Attestation de ramonage</Text>
                  <Text style={{fontSize: 11, color: "#666"}}>(Décret 2023-641)</Text>
                </View>
                <View style={{alignItems: "flex-end"}}>
                  <Text
                    style={{fontSize: 12, fontWeight: "bold", color: pdfStyle.color ?? "#333"}}
                  >
                    N° {data.certificateNumber}
                  </Text>
                  <Text style={styles.textSmall}>Le {dateParsed}</Text>
                </View>
              </View>

              <Text style={styles.sectionTitle}>INFORMATIONS CLIENT</Text>
              <View style={styles.gridRow}>
                <View style={styles.gridCell}>
                  <Text style={styles.cellLabel}>Client</Text>
                  <Text style={styles.cellValue}>{getLabel(customer)}</Text>
                </View>
                <View style={styles.gridCell}>
                  <Text style={styles.cellLabel}>Adresse</Text>
                  <Text style={styles.cellValue}>
                    {customer.address ?? customer.serviceAddress ?? ""},{" "}
                    {customer.zip ?? customer.serviceZip ?? ""}{" "}
                    {customer.city ?? customer.serviceCity ?? ""}
                  </Text>
                </View>
              </View>
              {hasPhotos && appareilBanner}
            </View>
            {appareilCount > 1 && !hasPhotos && appareilBanner}

            <AppareilSections
              appareil={appareil}
              styles={styles}
              dateParsed={dateParsed}
              intervenantName={intervenantName}
              appareilIndex={index}
            />

            {isLastAppareil ? (
              <View wrap={false} style={{marginTop: "auto"}}>
                <View
                  style={{
                    marginBottom: 12,
                    padding: "4 6",
                    backgroundColor: "#f8f8f8",
                  }}
                >
                  <Text style={styles.textSmall}>
                    La fréquence du ramonage doit respecter la réglementation applicable, notamment{" "}
                    <Link
                      src="https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000047947047"
                      style={{color: "#333", textDecoration: "underline"}}
                    >
                      l’article R. 1331-71
                    </Link>{" "}
                    du Code de la santé publique et les prescriptions locales. Renseignez-vous
                    auprès de votre mairie ou de votre préfecture.
                  </Text>
                  <Text style={styles.textSmall}>
                    L’entretien de l’appareil doit également être réalisé conformément à la
                    réglementation applicable, notamment pour son nettoyage, le contrôle de son bon
                    fonctionnement et, si nécessaire, son réglage.
                  </Text>
                  <Text style={styles.textSmall}>
                    L’utilisation des installations de chauffage au bois doit respecter les règles
                    de sécurité en vigueur.
                  </Text>
                </View>

                <View style={styles.signatureSection}>
                  <View style={styles.signatureBlock}>
                    <Text style={{fontSize: 8, fontWeight: "bold", marginBottom: 4}}>
                      Signature du technicien
                    </Text>
                    {data.signatureTechnicien && (
                      <Image src={data.signatureTechnicien} style={styles.signatureImage} />
                    )}
                    <Text style={styles.textSmall}>
                      Le {dayjs(data.signatureTechnicienDate).format("DD/MM/YYYY à HH:mm")}
                    </Text>
                  </View>
                  <View style={styles.signatureBlock}>
                    <Text style={{fontSize: 8, fontWeight: "bold", marginBottom: 4}}>
                      Signature du client
                    </Text>
                    {data.clientRefuseSigner ? (
                      <Text style={{fontSize: 8, marginTop: 8, color: "#666"}}>
                        Le client a refusé de signer
                      </Text>
                    ) : (
                      <>
                        {data.signatureClient && (
                          <Image src={data.signatureClient} style={styles.signatureImage} />
                        )}
                        {data.signatureClientDate && (
                          <Text style={styles.textSmall}>
                            Le {dayjs(data.signatureClientDate).format("DD/MM/YYYY à HH:mm")}
                          </Text>
                        )}
                      </>
                    )}
                  </View>
                </View>
              </View>
            ) : (
              <Text style={styles.continuation}>
                {isSecondToLastAppareil
                  ? `Suite : appareil ${index + 2} sur ${appareilCount}, mentions réglementaires et signatures en fin d’attestation`
                  : `Suite : appareil ${index + 2} sur ${appareilCount}`}
              </Text>
            )}

            {/* Footer */}
            <View
              {...fixedProps}
              style={[
                styles.footer,
                ...(repeatedLayout
                  ? [{position: "absolute" as const, bottom: "8mm", left: "10mm", right: "10mm"}]
                  : []),
              ]}
            >
              <Text>
                {companyAddress}
                {company.phone ? ` - Tél ${company.phone}` : ""}
                {company.email ? ` - ${company.email}` : ""}
              </Text>
              {(legal.companyType || legal.companyCapital || legal.siret) && (
                <Text>
                  {legal.companyType ?? ""}
                  {legal.companyCapital ? ` au capital de ${legal.companyCapital} €` : ""}
                  {legal.siret ? ` - SIRET ${legal.siret}` : ""}
                </Text>
              )}
              <Text>
                {legal.companyType ? `${legal.companyType} ${legal.legalName}` : legal.legalName}
              </Text>
            </View>

            {repeatedLayout && (
              <Text
                fixed
                style={styles.pageNumber}
                render={({pageNumber, totalPages}) => `${pageNumber} / ${totalPages}`}
              />
            )}
          </Page>
        );
      })}
    </Document>
  );
}

function AppareilSections({
  appareil,
  styles,
  dateParsed,
  intervenantName,
  appareilIndex,
}: {
  appareil: Appareil;
  styles: any;
  dateParsed: string;
  intervenantName?: string;
  appareilIndex: number;
}) {
  const tirageLabel =
    appareil.tirage === "conforme"
      ? "Conforme"
      : appareil.tirage === "nonConforme"
        ? "Non conforme"
        : "Non vérifié";

  return (
    <>
      <Text style={styles.sectionTitle}>CARACTÉRISTIQUE DE L&apos;INSTALLATION</Text>
      <View style={styles.gridRow}>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Type d&apos;appareil</Text>
          <Text style={styles.cellValue}>{getAppareilTypeLabel(appareil)}</Text>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Marque / Modèle</Text>
          <Text style={styles.cellValue}>
            {appareil.appareilMarque ?? "-"} / {appareil.appareilModele ?? "-"}
          </Text>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Combustible / Puissance</Text>
          <Text style={styles.cellValue}>
            {appareil.combustible} / {appareil.appareilPuissance ?? "-"}
          </Text>
        </View>
      </View>

      <View style={styles.gridRow}>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Type conduit</Text>
          <Text style={styles.cellValue}>{appareil.conduitType}</Text>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Arrivée d&apos;air</Text>
          <Text style={styles.cellValue}>{appareil.arriveeAir}</Text>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Emplacement</Text>
          <Text style={styles.cellValue}>{appareil.appareilEmplacement}</Text>
        </View>
      </View>

      <View style={styles.gridRow}>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>N° série</Text>
          <Text style={styles.cellValue}>{appareil.appareilNumeroSerie ?? "-"}</Text>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Date d'installation</Text>
          <Text style={styles.cellValue}>{appareil.appareilDateInstallation ?? "-"}</Text>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Garantie</Text>
          <Text style={styles.cellValue}>{appareil.appareilGarantie ?? "-"}</Text>
        </View>
      </View>

      <Text style={styles.sectionTitle}>INTERVENTION ET CONTRÔLE</Text>

      <View style={styles.gridRow}>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Nature de l'intervention</Text>
          {/* Champ tableau requis par le schéma, mais des attestations stockées avant durcissement de la validation peuvent l'avoir vide/absent. */}
          <Text style={styles.cellValue}>
            {(appareil.natureIntervention ?? []).join(", ") || "-"}
          </Text>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Date de l'intervention</Text>
          <Text style={styles.cellValue}>{dateParsed}</Text>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Intervenant</Text>
          <Text style={styles.cellValue}>{intervenantName}</Text>
        </View>
      </View>

      <View style={styles.gridRow}>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Contrôle visuel</Text>
          <View style={{flexDirection: "row", alignItems: "center", gap: 16}}>
            <View style={{flexDirection: "row", alignItems: "center"}}>
              <Text style={styles.cellValue}>de l'appareil</Text>
              <PdfCheck checked={appareil.controleVisuelAppareil === "oui"} />
            </View>
            <View style={{flexDirection: "row", alignItems: "center"}}>
              <Text style={styles.cellValue}>du conduit</Text>
              <PdfCheck checked={appareil.controleVisuelConduit === "oui"} />
            </View>
            <View style={{flexDirection: "row", alignItems: "center"}}>
              <Text style={styles.cellValue}>de la souche</Text>
              <PdfCheck checked={appareil.controleVisuelSouche === "oui"} />
            </View>
          </View>
        </View>
      </View>

      <View style={styles.gridRow}>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Nettoyage du conduit</Text>
          <Text style={styles.cellValue}>
            {appareil.nettoyageAction} - {appareil.nettoyageOutil}
          </Text>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Accès du nettoyage</Text>
          <Text style={styles.cellValue}>
            {(appareil.nettoyageMethodeAcces ?? []).join(", ") || "-"}
          </Text>
        </View>
      </View>

      <View style={styles.gridRow}>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Aspiration et évacuation des suies</Text>

          <View style={{flexDirection: "row", alignItems: "center"}}>
            <PdfCheck checked={appareil.aspirationSuies} />
          </View>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Tirage</Text>
          <Text style={styles.cellValue}>{tirageLabel}</Text>
        </View>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>État du conduit</Text>

          <Text style={styles.cellValue}>{appareil.etatConduit}</Text>
        </View>
      </View>

      {appareil.typeSuies || appareil.volumeSuies ? (
        <View style={styles.gridRow}>
          {appareil.typeSuies ? (
            <View style={styles.gridCell}>
              <Text style={styles.cellLabel}>Type de suies</Text>
              <Text style={styles.cellValue}>{appareil.typeSuies}</Text>
            </View>
          ) : null}
          {appareil.volumeSuies ? (
            <View style={styles.gridCell}>
              <Text style={styles.cellLabel}>Volume de suies</Text>
              <Text style={styles.cellValue}>{appareil.volumeSuies}</Text>
            </View>
          ) : null}
        </View>
      ) : null}

      {appareil.remarques && (
        <View style={{marginTop: 2, marginBottom: 4}}>
          <Text style={styles.cellLabel}>Remarques :</Text>
          <Text style={styles.text}>{appareil.remarques}</Text>
        </View>
      )}

      <RamonagePhotos photos={appareil.photos ?? []} appareilIndex={appareilIndex} />

      <View style={styles.gridRow}>
        <View style={styles.gridCell}>
          <Text style={styles.cellLabel}>Après ramonage de votre installation </Text>
          <Text style={styles.cellValue}>
            {" "}
            {appareil.attestationVacuite
              ? "Atteste la vacuité du conduit sur tout son parcours"
              : "Met en évidence une obstruction du conduit nécessitant une intervention avant toute utilisation de l’appareil"}
          </Text>
        </View>
      </View>
    </>
  );
}

function PdfCheck({checked}: {checked: boolean}) {
  return (
    <View
      style={{
        width: 10,
        height: 10,
        border: "1pt solid #333",
        marginLeft: 4,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {checked && <Text style={{fontSize: 8, fontWeight: "bold", lineHeight: 1}}>X</Text>}
    </View>
  );
}
